// info.js
import './info.css';
import { X } from 'lucide-react';
import React, { useRef, useState, useEffect } from 'react';
import overviewImg from '../assets/overview-numbered.png';
import topBar from '../assets/topbar-numbered.png';
import bottom from '../assets/bottom-numbered.png';
import TutorialOverlay from './tutorialOverlay';
import UpdatesPopup from "../components/UpdatesPopup";


const InfoPopup = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('walkthrough');
  const [tourOpen, setTourOpen] = useState(false);
  const [isUpdatesOpen, setIsUpdatesOpen] = useState(false);
  const [closedByTour, setClosedByTour] = useState(false);
  const prevIsOpenRef = useRef(isOpen);

  useEffect(() => {
    if (!prevIsOpenRef.current && isOpen) {
      setClosedByTour(false);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  const handleTourClose = () => {
    setTourOpen(false);
    // do not call onClose here to avoid toggling parent open again
  };

  const startTour = () => {
    setClosedByTour(true);
    setTourOpen(true);
    onClose?.();
  };

  const effectiveOpen = isOpen && !closedByTour;

  // if parent closed it, bail unless tour is running
  if (!effectiveOpen && !tourOpen) return null;

  // When tour is open: hide the popup and just render the overlay over the app.
  if (tourOpen) {
    return (
      <TutorialOverlay
        isOpen
        onClose={handleTourClose}
        rootSelector="#root"
      />
    );
  }
  if (isUpdatesOpen) {
    return (
      <UpdatesPopup
              isOpen={isUpdatesOpen}
              onClose={() => setIsUpdatesOpen(false)}
            />
    );
  }

  // Normal popup UI
  return (
    <div
      className="info-popup-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Sandbox Info"
      data-tour-ignore                         // never highlight the popup itself
    >
      <div className="info-popup">
      <button
          className="close-button close-button--abs"
          onClick={onClose}
          aria-label="Close info dialog"
          title="Close"
        >
          <X size={18} />
        </button>
        
        <h1>Notes on Experimental Sandbox</h1>

        <div className="info-tabs">
          
          {/* <button
            className={activeTab === 'walkthrough' ? 'active' : ''}
            onClick={() => setActiveTab('walkthrough')}
            aria-selected=
            {activeTab === 'walkthrough'}
          >
            Walkthrough
          </button> */}
          <button
            onClick={() => setIsUpdatesOpen(true)}
          >
            🔔 What's New
          </button>
          <button
            className={activeTab === 'walkthrough' ? 'active' : ''}
            onClick={() => setActiveTab('walkthrough')}
            aria-selected=
            {activeTab === 'walkthrough'}
          >
            About
          </button> 
          {/* Layout Tutorial button removed from tabs */}
          <button
            className={activeTab === 'masking' ? 'active' : ''}
            onClick={() => setActiveTab('masking')}
            aria-selected={activeTab === 'masking'}
          >
            Image Masking
          </button>
          <button
            className={activeTab === 'fileupload' ? 'active' : ''}
            onClick={() => setActiveTab('fileupload')}
            aria-selected={activeTab === 'fileupload'}
          >
            File Upload Modes
          </button>
        </div>

        <div className="tab-content">
          {/* {activeTab === 'overview' && (
            <div className="tab-pane">
              <h2>Main Page</h2>

              <div className="overview-grid">
                <figure className="overview-figure overview-figure--sticky">
                  <img
                    className="overview-img"
                    src={overviewImg}
                    alt="Labeled overview of the sandbox UI"
                    loading="lazy"
                  />
                  <figcaption>Quick reference of the main controls.</figcaption>
                </figure>

                <ol className="overview-callouts">
                  <li><strong>Model Selection:</strong> Choose which LLM model responds.</li>
                  <li><strong>Image Generation toggle:</strong> Enables/disables image generation with your prompt.</li>
                  <li><strong>System Prompt editor:</strong> Edit the assistant's system message.</li>
                  <li><strong>Upload files:</strong> Attach documents or images.</li>
                  <li><strong>Transcription:</strong> Upload audio files to generate a transcript.</li>
                  <li><strong>User prompt:</strong> Type your message here.</li>
                  <li><strong>PowerPoint generator:</strong> Creates slides from the uploaded files following LKC Medicine's template.</li>
                  <li><strong>Send message:</strong> Send your prompt.</li>
                  <li><strong>Clear chat:</strong> Reset the conversation.</li>
                </ol>
              </div>
            </div>
          )} */}

          {activeTab === 'walkthrough' && (
            <div className="tab-pane">
              <h2>About this Sandbox</h2>
              <p>
                Welcome to the experimental AI Sandbox. This platform is designed to provide a unified interface for interacting with a wide variety of Large Language Models (LLMs).
              </p>
              <p>
                <strong>Key Features:</strong>
              </p>
              <ul>
                <li><strong>Multi-Model Support:</strong> Switch seamlessly between models from providers like OpenAI, Anthropic, Google, and open-source alternatives to compare their reasoning and creative capabilities.</li>
                <li><strong>Multimodal Capabilities:</strong> Beyond text, you can generate images, upload documents for analysis (RAG & Context modes), and transcribe audio files.</li>
                <li><strong>Advanced Tools:</strong> Utilize features like PowerPoint generation from documents and image masking/inpainting for precise edits.</li>
              </ul>
              
              <p className="tour-prompt-text">
                New to the interface? Take a quick interactive tour to learn where everything is located.
              </p>
              <div className="start-tour-wrapper">
                <button
                  onClick={startTour}
                  className="start-tour-btn"
                >
                  Start Layout Tutorial
                </button>
              </div>
            </div>
          )}

          {activeTab === 'masking' && (
            <div className="tab-pane">
              <h2>Mask/Inpainting for Editing Images</h2>
              <p>
                Mask is a soft mask — images are completely regenerated as close as possible with the changes.
                Only 1 image can be edited at a time. The mask button will only be visible when a single image is uploaded.
                <br /><strong>Reset the chat after using the mask for best results.</strong>
              </p>
              <ul>
                <li><strong>Draw to edit:</strong> Use your mouse to draw <strong>black</strong> areas to mark regions to be changed, while everything white will be <strong>preserved</strong>.</li>
                <li><strong>Save mask:</strong> Click "Save Mask" after you are done drawing.</li>
                <li><strong>Clear:</strong> Clears any existing mask.</li>
                <li><strong>Upload mask:</strong> The mask image must be a PNG with alpha; it will be auto-resized to match the original. Masked area RGBA: (0, 0, 0, 255).</li>
              </ul>
            </div>
          )}

          {activeTab === 'fileupload' && (
            <div className="tab-pane">
              <h2>Understanding File Upload Modes</h2>

              <h3>Context Mode</h3>
              <p>This is the default mode for handling your documents.</p>
              <ul>
                <li><strong>How it works:</strong> Your browser extracts the text from your documents and sends that text to the AI.</li>
                <li><strong>GPT-4o/4o-mini enhancement:</strong> Uses direct upload so the AI can see images/formatting.</li>
                <li><strong>Special handling:</strong> With GPT-4o/4o-mini, images are sent directly; text is still extracted.</li>
                <li><strong>Important note:</strong> Switching away from GPT-4o/4o-mini removes images/directly processed files.</li>
                <li><strong>Privacy:</strong> Text files stay in your browser; only extracted content is sent.</li>
                <li><strong>Works with:</strong> Word, PowerPoint, PDFs, ZIPs containing those; images only with GPT-4o/4o-mini.</li>
                <li><strong>Best for:</strong> General questions and image-heavy files (with GPT-4o/4o-mini).</li>
              </ul>

              <h3>Strict Text (RAG) Mode</h3>
              <p>Acts like a smart search over your docs.</p>
              <ul>
                <li><strong>How it works:</strong> Files upload to secure storage, are indexed, and only relevant chunks are sent to the AI.</li>
                <li><strong>Text only:</strong> Not ideal for image-heavy presentations.</li>
                <li><strong>Best when:</strong> You need precise retrieval from text-heavy docs.</li>
                <li><strong>Benefits:</strong> Handles larger corpora and shows sources/similarity scores.</li>
                <li><strong>Privacy:</strong> Files are stored temporarily for processing.</li>
              </ul>

              <h3>Key Differences</h3>
              <ul>
                <li><strong>Processing:</strong> Context sends all extracted text; RAG sends only relevant parts.</li>
                <li><strong>Capacity:</strong> RAG scales better for big/many docs.</li>
                <li><strong>Traceability:</strong> RAG provides similarity scores and source snippets.</li>
              </ul>

              <p><strong>Important:</strong> Switching between Context and RAG clears uploaded files. Switching away from GPT-4o/4o-mini removes images/directly processed files.</p>
            </div>
          )}
        </div>

        {/* <button onClick={onClose}>Close</button> */}
      </div>
    </div>
  );
};

export default InfoPopup;
